import React, { useState, memo, forwardRef } from 'react';
import { oneOf, bool, node, string, func, element } from 'prop-types';
import classnames from 'classnames';

import { Button, Typography } from '@material-ui/core';

import useStyles from './useStyles';

const ThemedButton = forwardRef(
  (
    {
      active = false,
      children,
      className = '',
      color = 'primary',
      component = 'button',
      disabled = false,
      icon,
      onClick = () => {},
      size = 'standard',
      startIcon = null,
      variant = 'contained',
      title = '',
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [pressed, setPressed] = useState(false);
    const themed = useStyles();
    const classes = classnames(
      className,
      themed.Button,
      'ThemedButton',
      `Button--${color}`,
      `Button--${size}`,
      themed[`Button_${size}`],
      themed[`Button_${color}`],
      themed[`Button_${variant}`],
      {
        'Button--icon': !!icon && !children,
        'Button--focused': focused,
        'Button--pressed': pressed,
        [themed.Button_active]: active,
      }
    );
    const typographyClasses = classnames(
      themed.Typography,
      themed[`Typography_${color}`],
      themed[`Typography_${size}`],
      {
        'Typography--outlined': variant === 'outlined',
        'Typography--hover': hovered,
        'Typography--focus': focused,
        'Typography--link': variant === 'text',
      }
    );
    const iconClasses = classnames(themed.Button__Icon, `Button__Icon--${size}`, {
      [themed.Button__Icon_only]: !!icon && !children,
    });

    return (
      <Button
        ref={ref}
        disabled={disabled}
        disableElevation
        disableFocusRipple
        variant={variant}
        title={title}
        component={component}
        onClick={onClick}
        startIcon={startIcon}
        type="button"
        onMouseDown={() => {
          setPressed(true);
        }}
        onMouseUp={() => {
          setPressed(false);
        }}
        onMouseEnter={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
        }}
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={() => {
          setFocused(false);
        }}
        className={`${classes}`}
      >
        {icon && React.cloneElement(icon, { className: iconClasses })}
        {children && <Typography className={typographyClasses}>{children}</Typography>}
      </Button>
    );
  }
);

ThemedButton.propTypes = {
  active: bool,
  children: node,
  className: string,
  component: string,
  color: oneOf(['primary', 'secondary']),
  disabled: bool,
  onClick: func,
  size: oneOf(['standard', 'small', 'large', 'wide']),
  startIcon: element,
  icon: element,
  variant: oneOf(['outlined', 'contained', 'text']),
};

export default memo(ThemedButton);
